// Roles, and exactly what each one can do.
//
// Two levels, deliberately, because they answer different questions and are held
// by different people:
//
//   WORKSPACE — who is allowed in at all, and who may appoint others. Admins.
//   PROJECT   — what someone may do on a board. Viewer up to Project manager.
//
// A project manager runs their board without being able to add or remove people
// from the workspace, which is how established trackers split it. The two live in
// separate documents, and that is what makes the split enforceable rather than
// merely described: a manager may write project roles, and cannot reach admin
// because admin is not stored anywhere a manager can write.
//
// Every rule here is ALSO enforced in firestore.rules. This file decides what the
// interface offers; the database decides what is actually permitted. Hiding a
// button is convenience — the rules are the security.

/**
 * What someone may do on a board. Stored one document per person.
 *
 * `vendor` is the odd one out, and deliberately so. An outside supplier works on
 * ONE board — they move its cards, comment and raise issues like a member — but
 * must never reach the plan, the money, another board, or the settings. That is
 * not a rung on the ladder: it needs a MEMBER's power over issues while being
 * HIDDEN from surfaces a member sees. So it is NOT in `PROJECT_ROLES` (the normal
 * role picker never offers it — a vendor is GRANTED through the vendor-access
 * flow), its capabilities come from an explicit set rather than its rank (see
 * `can` below), and its rank is pinned LOW so any check that still reads the
 * ladder treats it as the least privileged, never accidentally a member. Every
 * one of these rules is ALSO enforced in the database — the app only decides what
 * is offered; the RLS decides what a vendor can actually read and write.
 */
export type ProjectRole = 'viewer' | 'reviewer' | 'member' | 'manager' | 'vendor';

/** The project roles, plus workspace admin, which is held separately. */
export type Role = ProjectRole | 'admin';

/** The assignable ladder offered in the role pickers — vendor is NOT here; it is
    granted through the vendor-access flow, not chosen like an ordinary role. */
export const PROJECT_ROLES: ProjectRole[] = ['viewer', 'reviewer', 'member', 'manager'];
/** Every role that can exist, so `toRole` preserves a vendor row (it would
    otherwise fall to viewer) and the capability grid can show the vendor column. */
export const ROLES: Role[] = [...PROJECT_ROLES, 'vendor', 'admin'];

export const RANK: Record<Role, number> = {
  viewer: 1,
  // Below member on the ladder: a vendor's issue rights come from the explicit
  // set in `can`, NOT this number, which only pins it low so any lingering rank
  // check keeps it the least privileged rather than mistaking it for a member.
  vendor: 2,
  reviewer: 2,
  member: 3,
  manager: 4,
  admin: 5,
};

export const ROLE_LABEL: Record<Role, string> = {
  viewer: 'Viewer',
  reviewer: 'Reviewer',
  member: 'Member',
  manager: 'Project Manager',
  vendor: 'Vendor',
  admin: 'Admin',
};

export const ROLE_BLURB: Record<Role, string> = {
  viewer: 'Reads everything. Cannot comment or change anything.',
  reviewer: 'Reads everything, comments, and can raise new issues.',
  member: 'Does the work — edits issues and moves them between columns.',
  manager:
    'Runs this project: its settings, what everyone on it may do, and can delete issues. Cannot add or remove people, or appoint admins.',
  vendor:
    'An outside supplier working on this one board: sees and updates its issues, raises new ones, and reads the documents shared with them. Cannot reach the plan, the budget, any other board, or the settings.',
  admin:
    'Everything a Project Manager can do, plus adding and removing people and appointing other admins.',
};

/** Anything not on the ladder is treated as the lowest rung. */
export function toRole(value: unknown): Role {
  return typeof value === 'string' && (ROLES as string[]).includes(value)
    ? (value as Role)
    : 'viewer';
}

export type Capability =
  | 'read'
  | 'comment'
  | 'answerReport'
  | 'createIssue'
  | 'editIssue'
  | 'moveIssue'
  | 'deleteIssue'
  | 'removeAnyComment'
  | 'editSettings'
  | 'answerForSomebody'
  | 'sendSigningLink'
  | 'setRoles'
  | 'askToAdd'
  | 'createProject'
  | 'configureAssists'
  | 'manageWorkspace';

const NEEDS: Record<Capability, number> = {
  read: 1,
  comment: 2,
  /*
   * SPEAKING TO A CUSTOMER IS NOT COMMENTING, and it sits a rank higher for
   * that reason alone. A reviewer may say anything they like to us on a card;
   * an answer that leaves the building and lands in somebody else's inbox,
   * signed FlightDeck, is a different promise. Kept in step with the same check
   * in supabase/functions/reply, which is the one that actually enforces it —
   * this only decides whether the box is offered.
   */
  answerReport: 3, // member
  createIssue: 2,
  editIssue: 3,
  moveIssue: 3,
  deleteIssue: 4, // manager
  removeAnyComment: 5, // admin — anyone else's words are not theirs to withdraw
  editSettings: 4, // manager
  /*
   * RECORDING A SIGN-OFF FOR SOMEBODY WHO COULD NOT GIVE IT.
   *
   * Everybody on a sign-off panel is a NAME rather than an account — a client's
   * sponsor is not going to be given a login — so an answer arrives by email or
   * in a meeting and somebody types it in. That is the ordinary case here, not
   * the exception.
   *
   * Admin-only at first, which meant every answer on TRINRE's board waited on
   * one person being at a keyboard. Vinnie, 22 August: *"a Project Manager
   * should be able to record an answer for someone who's away seeing as they
   * are the project manager managing this project/board"*. They already run the
   * board's settings and its panel of approvers; being unable to write down an
   * answer to the thing they administer was the odd rule, not this.
   *
   * A member cannot, and that is the line: doing the work is not the same as
   * recording somebody else's approval of it.
   */
  answerForSomebody: 4, // manager
  /*
   * SENDING SOMEBODY A LINK TO SIGN WITH — #216.
   *
   * The same rung as recording an answer for them, and for the same reason: a
   * link IS the credential, so handing one out is handing somebody the ability
   * to put a name on a sheet. Whoever runs the board decides who is asked; a
   * member who is doing the work does not.
   */
  sendSigningLink: 4, // manager
  setRoles: 4, // manager — project roles only; admin is out of reach by design
  /*
   * ASKING AN ADMIN TO ADD SOMEBODY — #359, widened.
   *
   * Only admins invite; everybody else on a board ASKS one to. The floor is a
   * MEMBER, matching the row policy exactly (`board_requests_ask` accepts
   * my_rank_on(board) >= 3): a member who is doing the work can pull a
   * colleague onto their board, or ask for an assistant to help run it, and an
   * admin decides. A reviewer or a viewer cannot — the same line the RLS draws.
   * Deliberately BELOW setRoles: asking is not managing, and the whole point is
   * that a member who cannot change a single role may still ask for help.
   */
  askToAdd: 3, // member
  /*
   * A BOARD OF YOUR OWN IS NOT THE SAME RIGHT AS RUNNING SOMEBODY ELSE'S.
   *
   * Every other level here is about what you may do to work that already
   * exists, so it climbs: the more you may change of other people's, the
   * higher you sit. Making a board of your own changes nobody else's anything,
   * and it is the one thing somebody invited to try the software has to be
   * able to do before they can judge it at all.
   *
   * A reviewer can already raise an issue, which is a larger act on somebody
   * else's work than starting an empty board is on theirs. What the creator
   * may then DO on that board is not this rank — it is ownership, checked in
   * firestore.rules against the board's own `owner`.
   */
  createProject: 2, // reviewer — see ownsProject in firestore.rules
  /*
   * TURNING THE BOARD'S ADAPTIVE ASSISTS ON OR OFF — the card size/date assists
   * and the Insights Data quality panel (project-idle-nudge). The ordinary board
   * controls (staging, the dependency block, scope approval) sit at manager
   * (editSettings=4). Vinnie put THIS one a rung higher, at admin: the assists
   * read a board's own shape to help everyone on it, so whether a board runs them
   * at all is a board-config decision kept to an admin, not a per-project manager.
   * The only other rank-5 right is the workspace itself; this is its board-level
   * sibling. The flips are recorded in the board's control trail (0205).
   */
  configureAssists: 5, // admin — a board-config privilege above manager
  manageWorkspace: 5, // admin — inviting, removing, appointing admins
};

/**
 * A VENDOR'S RIGHTS ARE A LIST, NOT A RANK.
 *
 * Exactly the work on the board and nothing around it: read it, comment, raise an
 * issue, edit an issue, move it between columns. Everything else — deleting,
 * settings, sign-offs, asking to add people, starting a board, the workspace — is
 * absent, because a vendor sits below member on the ladder, and these are the five
 * things a member does that an outside supplier doing the work also needs. Kept as
 * a set so adding a new capability does NOT silently grant it to vendors: a vendor
 * gets a capability only by being named here. Mirrored by the database — a vendor
 * is held at the lowest rank there and admitted to just these writes by name.
 */
const VENDOR_CAN: ReadonlySet<Capability> = new Set<Capability>([
  'read',
  'comment',
  'createIssue',
  'editIssue',
  'moveIssue',
]);

export function can(role: Role, capability: Capability): boolean {
  if (role === 'vendor') return VENDOR_CAN.has(capability);
  return RANK[role] >= NEEDS[capability];
}

/**
 * WHO SEES THE COMPLETION ODDS — the release/sprint "chance of making it" read (Layer B).
 *
 * This is NOT a fixed-rank capability, because a board can widen it: it is a management read by
 * default (a Project Manager and above, who runs the board and reads the forecast), but an admin
 * can open it to everyone on the board with the `show_odds_to_everyone` setting (0220). So the
 * gate takes the board's setting as well as the role:
 *   · setting ON  → everyone on the board sees it;
 *   · setting OFF → a Project Manager (rank 4) and above only — admin included, a vendor excluded.
 * The one place every surface (the card modal, the sprint strip) reads this, so the rule cannot
 * drift between them. It governs visibility only; it never changes a number.
 */
export function canSeeCompletionOdds(role: Role, showToEveryone: boolean): boolean {
  if (showToEveryone) return true;
  return RANK[role] >= RANK.manager;
}

/**
 * Whether this person may take THIS comment off the card.
 *
 * Two separate rights, and they are not a rank. An admin may remove anybody's,
 * because somebody has to be able to. Everyone may remove their own, whatever
 * their role — words you wrote are yours to withdraw, and needing to ask an
 * admin to unsay something is a poor way to treat a reviewer.
 *
 * Nobody else, at any rank: a manager can delete a whole issue and still cannot
 * quietly edit what somebody said on it.
 */
export function canRemoveComment(role: Role, commentAuthor: string, me: string): boolean {
  if (can(role, 'removeAnyComment')) return true;
  const mine = (me ?? '').trim().toLowerCase();
  return !!mine && (commentAuthor ?? '').trim().toLowerCase() === mine;
}

/**
 * Whether this person may EDIT this comment or status update — reword it.
 *
 * The author, and ONLY the author. This is deliberately NOT `canRemoveComment`:
 * removing is moderation (an admin may withdraw anybody's, because somebody has
 * to be able to), but editing is changing what a person said, and nobody —
 * admin included — may quietly put words in someone else's mouth. It is the rule
 * canRemoveComment's own note already described; this restores it for the edit
 * action, which until now borrowed the removal check and let an admin reword
 * another person's entry. Role is irrelevant, hence unused.
 */
export function canEditComment(_role: Role, commentAuthor: string, me: string): boolean {
  const mine = (me ?? '').trim().toLowerCase();
  return !!mine && (commentAuthor ?? '').trim().toLowerCase() === mine;
}

/** For the permissions table, so the rules are visible rather than guessed. */
export const CAPABILITY_LABEL: { key: Capability; label: string }[] = [
  { key: 'read', label: 'See the board and every issue' },
  { key: 'comment', label: 'Comment on an issue' },
  { key: 'createIssue', label: 'Raise a new issue' },
  { key: 'editIssue', label: 'Edit an issue’s details' },
  { key: 'moveIssue', label: 'Move an issue between columns' },
  { key: 'deleteIssue', label: 'Delete an issue' },
  { key: 'removeAnyComment', label: 'Remove anyone’s comment' },
  { key: 'editSettings', label: 'Change project settings' },
  { key: 'answerForSomebody', label: 'Record a sign-off for somebody who could not give it' },
  { key: 'sendSigningLink', label: 'Send a signatory a link to sign with' },
  { key: 'setRoles', label: 'Set what someone may do on this project' },
  { key: 'askToAdd', label: 'Ask an admin to add a person or an assistant' },
  { key: 'createProject', label: 'Start a board of their own' },
  { key: 'manageWorkspace', label: 'Add or remove people, and appoint admins' },
];

/**
 * WHAT SOMEBODY MAY DO, ON ONE PARTICULAR BOARD.
 *
 * THE GAP THIS CLOSES. A role was held once, for the whole workspace:
 * `roles/{email}` held a single value and every check read that same value
 * whichever board was being touched. So a project manager was a manager
 * everywhere — of the shared Sandbox, of the feedback board, and of anybody
 * else's work they could open. The access grid could say who may OPEN which
 * board and had nothing to say about what they could then do on it.
 *
 * THE SHAPE, AND WHY IT IS NOT A SUBCOLLECTION. The same document grows a map:
 *
 *     roles/dominic@example.com = {
 *       role: 'reviewer',                     // where a board says nothing
 *       on: { website: 'manager', sandbox: 'viewer' }
 *     }
 *
 * A subcollection would have been the tidier model and costs two things this
 * cannot afford. Every permission check in firestore.rules would need a second
 * document read — and a rule is capped at ten — and reading them all in the app
 * would need a collection-group query whose name would collide with the
 * top-level `projects` collection.
 *
 * NOTHING NEEDS MIGRATING, which is the point of keeping `role`. It stays as
 * the default wherever a board has no entry of its own, so the day this ships
 * everybody has exactly the rights they had the day before. A half-migrated
 * board is never a locked-out board — the same tolerance that carried the last
 * role change, for the same reason.
 *
 * ADMIN IS NOT ON THIS LADDER and never comes from here. It lives in the
 * access document, which a manager cannot write, and it outranks everything
 * below — an admin who could be demoted on one board by a manager of that
 * board would not be an admin.
 */
export function effectiveRole(opts: {
  email?: string;
  admins: string[];
  /** One default role per person, used where a board has no entry. */
  defaults: Record<string, string>;
  /** Per person, per board. */
  on?: Record<string, Record<string, string>>;
  /** The older map that lived inside the access document. */
  legacy?: Record<string, string>;
  /** Which board is being asked about. Omit for a workspace-level question. */
  projectId?: string;
}): Role {
  const { email, admins, defaults, on = {}, legacy = {}, projectId } = opts;
  if (!email) return 'viewer';
  const e = email.toLowerCase();
  if (admins.includes(e)) return 'admin';
  if (legacy[e] === 'admin') return 'admin';
  // The board's own answer first, then the person's default, then the old map.
  const here = projectId ? on[e]?.[projectId] : undefined;
  return toRole(here ?? defaults[e] ?? legacy[e]);
}

/**
 * Whether a board has been given an answer of its own for this person.
 *
 * Shown rather than inferred, because "manager" in a cell means two different
 * things: chosen for this board, or inherited from everywhere else. Somebody
 * changing one board's roles needs to know which they are looking at, or they
 * will change a default believing they changed one board.
 */
export function roleIsSetHere(
  on: Record<string, Record<string, string>> | undefined,
  email: string,
  projectId: string
): boolean {
  return !!on?.[email.toLowerCase()]?.[projectId];
}
